import { Link } from 'react-router-dom';
import { BRAND } from '../lib/brand';
import { Mark } from './Mark';

/**
 * Header and footer, ported from `apps/web/components/SiteChrome.tsx`.
 *
 * Two differences, both forced by the deployment. Internal links are
 * react-router `<Link>`s, because this is one bundle and a full navigation
 * would refetch it. And there is no API link in the nav: `apps/web` points at
 * its own `/api/snapshot`, which does not exist on a static host — the paid
 * API is a separate service that has not been built yet, so the nav sends
 * people to the docs rather than to a 404.
 */

export type NavKey = 'alerts' | 'terminal' | 'honest';

const LINKS: { key: NavKey; href: string; label: string }[] = [
  { key: 'alerts', href: '/', label: 'Alerts' },
  { key: 'terminal', href: '/terminal', label: 'Terminal' },
  { key: 'honest', href: '/honest-numbers', label: 'Honest numbers' },
];

export function SiteHeader({ active }: { active: NavKey | null }) {
  return (
    <header>
      <div className="brandline">
        {/* The mark and the name are the way home, as on every other site (QA 29 Sep). */}
        <Link aria-label={`${BRAND.name} home`} className="homelink" to="/">
          <Mark size={26} />
          <span className="wordmark">{BRAND.name}</span>
        </Link>
        <span className="chip">
          <span className="dot" />
          24/7 · {BRAND.chain}
        </span>
      </div>
      <div className="navlinks">
        {LINKS.map((l) => (
          <Link className={l.key === active ? 'on' : undefined} key={l.key} to={l.href}>
            {l.label}
          </Link>
        ))}
        {/*
          The buy chip. Live only when brand.json has an official ponsfamily.com
          token page in `links.pons` (validated in lib/pons.ts); until then it is
          a disabled label, never a guessed link. It links out: the site has no
          custody and no order routing, so there is no swap or wallet here.
        */}
        {BRAND.links.pons ? (
          <a
            aria-label={`Buy ${BRAND.ticker} on ${BRAND.launchpad} (opens ${BRAND.launchpad} in a new tab). ${BRAND.tokenRisk}`}
            className="ticker-chip"
            href={BRAND.links.pons}
            rel="noreferrer noopener"
            target="_blank"
            title={BRAND.tokenRisk}
          >
            <span className="tc-long">
              Buy {BRAND.ticker} on {BRAND.launchpad}
            </span>
            <span aria-hidden="true" className="tc-short">
              Buy {BRAND.ticker}
            </span>
            <svg aria-hidden="true" fill="none" height="11" viewBox="0 0 16 16" width="11">
              <path
                d="M4 12L12 4M6 4h6v6"
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="1.7"
              />
            </svg>
          </a>
        ) : (
          <span aria-disabled="true" className="ticker-chip off">
            <span className="tc-long">
              {BRAND.ticker} · coming to {BRAND.launchpad}
            </span>
            <span className="tc-short">{BRAND.ticker}</span>
          </span>
        )}
      </div>
    </header>
  );
}

export function SiteFooter() {
  return (
    <footer>
      <div className="l">
        {BRAND.compliance}
        {BRAND.links.pons ? <span className="risk">{BRAND.tokenRisk}</span> : null}
      </div>
      <div className="r">
        <a href={BRAND.links.x} rel="noreferrer noopener" target="_blank">
          {BRAND.handle}
        </a>
        <a href={BRAND.links.telegram} rel="noreferrer noopener" target="_blank">
          Telegram
        </a>
        <Link to="/honest-numbers">Honest numbers</Link>
      </div>
    </footer>
  );
}
